import { useMemo } from "react";

import { useEffectiveEntityRight } from "@/domain/_shared/entity-permission/hooks/api/use-effective-entity-right";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ActionType, PermissionCheck, ResourceType } from "../../models/permission-types";
import { getEntityTypeForResource, hasRequiredRight } from "../../models/permission-v2-mapping";

import { checkPermissionListQueryDocument } from "./permission.gql";

interface UseCheckPermissionListProps {
  permissions: PermissionCheck[];
  enabled?: boolean;
}

export const useCheckPermissionList = ({ permissions, enabled = true }: UseCheckPermissionListProps) => {
  const queryClient = useQueryClient();
  const workspaceContext = useUnsafeCurrentWorkspaceContext();
  const { isEnabled: isV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });

  const firstPermission = permissions[0] as PermissionCheck | undefined;
  const entityType = firstPermission ? getEntityTypeForResource(firstPermission.resource) : null;
  const resourceId = firstPermission?.resourceId ?? "";
  const workspaceId = workspaceContext?.currentWorkspace.id ?? "";
  const useV2 = isV2Enabled && entityType !== null && !!workspaceId;

  if (process.env.NODE_ENV !== "production" && useV2 && permissions.length > 1) {
    const allSameResource = permissions.every((p) => p.resourceId === resourceId);
    if (!allSameResource) {
      console.warn("useCheckPermissionList v2: all permissions must share the same resourceId");
    }
  }

  const { effectiveRight, loading: v2Loading, error: v2Error } = useEffectiveEntityRight({
    entityId: resourceId,
    entityType: entityType ?? "",
    workspaceId,
    enabled: enabled && !!resourceId && useV2,
  });

  const query = useQuery({
    queryKey: queryKeys.permissions.checkList(
      permissions.map((p) => ({
        resource: p.resource,
        action: p.action,
        resourceId: p.resourceId || "",
      })),
    ),
    queryFn: async () => {
      const client = createOperationClient("CheckPermissionList");
      const data = await client.request(checkPermissionListQueryDocument, {
        input: {
          permissions: permissions.map((p) => ({
            resource: p.resource,
            action: p.action,
            resourceId: p.resourceId || "",
          })),
        },
      });
      return data.checkPermissionList;
    },
    enabled: enabled && permissions.length > 0 && !useV2,
    staleTime: 5 * 60 * 1000,
  });

  const v2PermissionRecord = useMemo(() => {
    if (!useV2) {
      return {};
    }

    const record: Partial<Record<ResourceType, Partial<Record<ActionType, boolean>>>> = {};
    for (const permission of permissions) {
      const permEntityType = getEntityTypeForResource(permission.resource);
      if (permEntityType) {
        if (!record[permission.resource]) {
          record[permission.resource] = {};
        }
        // Only apply effectiveRight when the permission's resourceId matches the one we
        // actually fetched. When multiple distinct resourceIds are passed, we cannot
        // determine the correct right for non-matching entries, so we default to false.
        const rightForPermission =
          permission.resourceId === resourceId ? effectiveRight : null;
        record[permission.resource]![permission.action] = hasRequiredRight(rightForPermission, permission.action);
      }
    }
    return record;
  }, [useV2, permissions, effectiveRight, resourceId]);

  const v1PermissionRecord = useMemo(() => {
    const record: Partial<Record<ResourceType, Partial<Record<ActionType, boolean>>>> = {};

    if (query.data) {
      query.data.forEach((result) => {
        const [resource, action] = result.id.split("-");

        if (!record[resource as ResourceType]) {
          record[resource as ResourceType] = {};
        }

        record[resource as ResourceType]![action as ActionType] = result.hasPermission;
      });
    }

    return record;
  }, [query.data]);

  if (useV2) {
    return {
      refetch: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.entityPermissions.all });
        return Promise.resolve();
      },
      loading: v2Loading,
      error: v2Error,
      permissionResultList: [],
      permissionRecord: v2PermissionRecord,
    };
  }

  return {
    refetch: query.refetch,
    loading: query.isLoading,
    error: query.error,
    permissionResultList: query.data ?? [],
    permissionRecord: v1PermissionRecord,
  };
};
